import axios from 'axios';

// Create an axios instance with default config
const api = axios.create({
  // Use production URL in production, localhost in development
  baseURL: process.env.NODE_ENV === 'production' 
    ? 'https://www.product-matching-app.com/api'
    : 'http://localhost:8080/api',
  withCredentials: true, // Important for cookies
  headers: {
    'Content-Type': 'application/json',
  }
});

// Response interceptor
api.interceptors.response.use(
  (response) => {
    // Return successful responses as-is
    return response;
  },
  (error) => {
    // Handle 401 Unauthorized errors
    if (error.response && error.response.status === 401) {
      console.log('Authentication error:', error.response.data.message);
      
      // Only redirect if we're not already on the login page
      if (!window.location.pathname.includes('/login')) {
        // Clear any stored auth data
        // We'll use localStorage to communicate with other parts of the app
        localStorage.setItem('auth_error', 'true');
        
        // Redirect to login page
        window.location.href = '/login';
      }
    }
    
    // Return the error for other error handlers
    return Promise.reject(error);
  }
);

// Authentication API calls
export const authAPI = {
  // Login with password
  login: async (password) => {
    const response = await api.post('/auth/login', {
      data: { password }
    });
    return response.data;
  },
  
  // Logout
  logout: async () => {
    const response = await api.post('/auth/logout');
    return response.data;
  },
  
  // Check authentication status
  checkAuth: async () => {
    const response = await api.post('/auth/me');
    return response.data;
  }
};

// Products API calls
export const productsAPI = {
  // Get all products
  getAllProducts: async () => {
    const response = await api.post('/products', {
      data: {}
    });
    return response.data;
  },
  
  // Get product by ID
  getProductById: async (id) => {
    const response = await api.post('/products', {
      data: { id }
    });
    return response.data;
  },
  
  // Get product by EAN
  getProductByEAN: async (ean) => {
    const response = await api.post('/products', {
      data: { ean }
    });
    return response.data;
  },
  
  // Get OBI product by article number
  getObiProductByArticleNumber: async (articleNumber) => {
    const response = await api.post('/getObiProductByArticleNumber', {
      data: { articleNumber }
    });
    return response.data;
  },
  
  // Report a missing EAN
  reportMissingEAN: async (ean) => {
    const response = await api.post('/addMissingEan', {
      data: { ean }
    });
    return response.data;
  },
  
  // Mark product as combinable with OBI product
  addKombinierbar: async (hagebauProductId, obiProductId) => {
    const response = await api.post('/addKombinierbar', {
      data: { hagebauProductId, obiProductId }
    });
    return response.data;
  },
  
  // Mark product as not combinable with OBI product
  addNichtKombinierbar: async (hagebauProductId, obiProductId) => {
    const response = await api.post('/addNichtKombinierbar', {
      data: { hagebauProductId, obiProductId }
    });
    return response.data;
  },
  
  // Reject product completely
  addReject: async (hagebauProductId, existsInObiMarket) => {
    const response = await api.post('/addReject', {
      data: { hagebauProductId, existsInObiMarket }
    });
    return response.data;
  },
  
  // Revert a product match
  revertMatch: async (hagebauProductId) => {
    const response = await api.post('/revertMatch', {
      data: { hagebauProductId }
    });
    return response.data;
  }
};

// Matches API calls
export const matchesAPI = {
  // Get all matches
  getAllMatches: async () => {
    const response = await api.post('/matches', {
      data: {}
    });
    return response.data;
  },
  
  // Get match by ID
  getMatchById: async (id) => {
    const response = await api.post('/matches', {
      data: { id }
    });
    return response.data;
  },
  
  // Create a new match
  createMatch: async (sourceProductId, targetProductId, matchScore) => {
    const response = await api.post('/matches', {
      data: {
        action: 'create',
        sourceProductId,
        targetProductId,
        matchScore
      }
    });
    return response.data;
  },
  
  // Update match status
  updateMatchStatus: async (id, matchStatus) => {
    const response = await api.post('/matches', {
      data: {
        action: 'update',
        id,
        matchStatus
      }
    });
    return response.data;
  }
};

export default api;
